import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Save } from 'lucide-react';
import { supabase } from '../supabase';
import { DataGrid, GridCol } from '../ui/DataGrid';
import { Button } from '../ui/arc/button';
import { Badge } from '../ui/arc/badge';
import { PageHeader, Notice, ScreenSkeleton } from './plata-kit';
import { KpiCard, MonthPicker, MoneyField, Panel, money, monthLabel } from './costsUi';
import { CABIFY_STATE_ES } from '../CabifyBatch';

// Costos & BI → Cabify (14/10/2026): cuánto se gasta en envíos de Cabify, mes a mes, para
// controlar la factura. La API de Cabify NO informa lo cobrado: cada envío se COTIZA con sus datos
// reales (destino, peso, medidas y la modalidad con que salió) — al despachar (webhook) o con
// "Cotizar faltantes" — y se guarda en cabify_deliveries.estimate (amount en centavos).
// El total de la factura de cada mes se carga a mano (cabify_invoices) y se compara.

type Row = {
    id: number; order_id: number | null; external_id: string | null; parcel_id: string; status: string;
    estimate: { amount: number; currency?: string; shipping_type?: string; modality?: string; source?: string } | null;
    created_at: string; raw: any;
};

// No se cobran: borrados/cancelados o etiquetas que nunca se despacharon
const NOT_CHARGED = new Set(['deleted', 'requestercancel', 'internalcancel', 'pickupfailed', 'created', 'ready']);

// Fecha del servicio: cuando Cabify lo tomó (primer evento de despacho); si no, la creación
const serviceDate = (r: Row): string => {
    const ev = (r.raw?.events || []).find((e: any) => e?.at && !/^(created|ready)$/.test(e.state || ''));
    return ev?.at || r.created_at;
};
const ym = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
const pesos = (cents: number) => cents / 100;

// Lo que pagó el cliente por el envío (orders.shipping_cost_customer). Un pedido de varios bultos
// se cobra UNA vez: el cobro va en su primer bulto y los demás dicen "2º bulto".
type Charge = { kind: 'cobrado' | 'gratis' | 'canje' | 'manual' | 'bulto'; amount: number; n?: number };
type OrderLite = { id: number; shipping_cost_customer: number | null; shipping_option: string | null; is_canje: boolean | null };

export function CabifyCostsView() {
    const [rows, setRows] = useState<Row[]>([]);
    const [ordersById, setOrdersById] = useState<Map<number, OrderLite>>(new Map());
    const [invoices, setInvoices] = useState<Record<string, { amount: number; notes: string | null }>>({});
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState('');
    const [invDraft, setInvDraft] = useState('');
    const [busy, setBusy] = useState<'' | 'pricing' | 'saving'>('');
    const [notice, setNotice] = useState<{ tone: 'success' | 'danger' | 'info'; text: string } | null>(null);

    const load = useCallback(async () => {
        const all: Row[] = [];
        for (let from = 0; from < 20000; from += 1000) {
            const { data } = await supabase.from('cabify_deliveries')
                .select('id, order_id, external_id, parcel_id, status, estimate, created_at, raw')
                .not('parcel_id', 'like', 'demo%').order('created_at', { ascending: false }).range(from, from + 999);
            all.push(...((data || []) as Row[]));
            if (!data || data.length < 1000) break;
        }
        const ids = Array.from(new Set(all.map(r => r.order_id).filter((x): x is number => x != null)));
        const om = new Map<number, OrderLite>();
        for (let i = 0; i < ids.length; i += 300) {
            const { data: od } = await supabase.from('orders').select('id, shipping_cost_customer, shipping_option, is_canje').in('id', ids.slice(i, i + 300));
            for (const o of (od || []) as OrderLite[]) om.set(o.id, o);
        }
        setOrdersById(om);
        const { data: inv } = await supabase.from('cabify_invoices').select('month, amount, notes');
        setRows(all);
        setInvoices(Object.fromEntries((inv || []).map((i: any) => [i.month, { amount: Number(i.amount), notes: i.notes }])));
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const charged = useMemo(() => rows.filter(r => !NOT_CHARGED.has(r.status)), [rows]);
    // cobro al cliente por fila: el primer bulto de cada pedido lleva el cobro del pedido
    const chargeOf = useMemo(() => {
        const first = new Map<number, Row>();
        for (const r of [...charged].sort((a, b) => a.created_at.localeCompare(b.created_at))) if (r.order_id != null && !first.has(r.order_id)) first.set(r.order_id, r);
        const m = new Map<number, Charge>();
        for (const r of charged) {
            if (r.order_id == null) { m.set(r.id, { kind: 'manual', amount: 0 }); continue; }
            if (first.get(r.order_id)?.id !== r.id) { m.set(r.id, { kind: 'bulto', amount: 0 }); continue; }
            const o = ordersById.get(r.order_id);
            const amt = Number(o?.shipping_cost_customer) || 0;
            m.set(r.id, o?.is_canje ? { kind: 'canje', amount: 0 } : amt > 0 ? { kind: 'cobrado', amount: amt } : { kind: 'gratis', amount: 0 });
        }
        return m;
    }, [charged, ordersById]);
    const months = useMemo(() => Array.from(new Set(charged.map(r => ym(serviceDate(r))))).sort().reverse(), [charged]);
    useEffect(() => { if (!month && months.length) setMonth(months[0]); }, [months, month]);
    useEffect(() => { setInvDraft(invoices[month] ? String(invoices[month].amount) : ''); }, [month, invoices]);

    const inMonth = useMemo(() => charged.filter(r => ym(serviceDate(r)) === month)
        .sort((a, b) => serviceDate(b).localeCompare(serviceDate(a))), [charged, month]);
    const priced = inMonth.filter(r => r.estimate?.amount != null);
    const total = priced.reduce((s, r) => s + pesos(r.estimate!.amount), 0);
    const unpriced = inMonth.length - priced.length;
    const invoice = invoices[month]?.amount;
    const diff = invoice != null ? invoice - total : null;
    // Cobrado vs costo del mes, por pedido (el costo de un pedido = suma de sus bultos)
    const recovery = useMemo(() => {
        const g = { cobrado: { orders: 0, cobrado: 0, costo: 0 }, gratis: { orders: 0, costo: 0 }, canje: { orders: 0, costo: 0 }, manual: { n: 0, costo: 0 } };
        const costByOrder = new Map<number, number>();
        for (const r of priced) if (r.order_id != null) costByOrder.set(r.order_id, (costByOrder.get(r.order_id) || 0) + pesos(r.estimate!.amount));
        for (const r of inMonth) {
            const c = chargeOf.get(r.id);
            if (!c || c.kind === 'bulto') continue;
            if (c.kind === 'manual') { g.manual.n++; g.manual.costo += r.estimate?.amount != null ? pesos(r.estimate.amount) : 0; continue; }
            const costo = costByOrder.get(r.order_id!) || 0;
            if (c.kind === 'cobrado') { g.cobrado.orders++; g.cobrado.cobrado += c.amount; g.cobrado.costo += costo; }
            else { g[c.kind].orders++; g[c.kind].costo += costo; }
        }
        const totalCobrado = g.cobrado.cobrado;
        return { ...g, totalCobrado, pct: total > 0 ? totalCobrado / total * 100 : 0, puesto: total - totalCobrado };
    }, [inMonth, priced, chargeOf, total]);
    const pendingAll = rows.filter(r => !NOT_CHARGED.has(r.status) && !r.estimate).length;
    const byModality = useMemo(() => {
        const m = new Map<string, { n: number; total: number }>();
        for (const r of priced) {
            const k = r.estimate?.shipping_type || 'sin modalidad';
            const cur = m.get(k) || { n: 0, total: 0 }; cur.n++; cur.total += pesos(r.estimate!.amount); m.set(k, cur);
        }
        return Array.from(m.entries()).sort((a, b) => b[1].total - a[1].total);
    }, [priced]);

    // Cotizar los que falten (cotizar es gratis: no despacha ni cobra), de a 40 por llamada
    const priceMissing = async () => {
        setBusy('pricing'); setNotice(null);
        let done = 0, errors = 0;
        try {
            for (let i = 0; i < 12; i++) {
                const { data, error } = await supabase.functions.invoke('cabify-logistics', { body: { action: 'price_parcels', limit: 40 } });
                if (error || !data?.ok) throw new Error(data?.error || error?.message || 'error');
                done += data.priced || 0; errors += (data.errors || []).length;
                if (!data.pending || !data.priced) break;
            }
            setNotice({ tone: errors ? 'info' : 'success', text: `Se cotizaron ${done} envíos${errors ? ` · ${errors} sin poder cotizar (sin datos de destino o todavía sin despachar)` : ''}.` });
        } catch (e: any) {
            setNotice({ tone: 'danger', text: 'No se pudo cotizar: ' + (e?.message || e) });
        }
        setBusy(''); load();
    };

    const saveInvoice = async () => {
        const n = Number(String(invDraft).replace(/\./g, '').replace(',', '.'));
        if (!month || !Number.isFinite(n) || n < 0) return;
        setBusy('saving');
        const { data: u } = await supabase.auth.getUser();
        const { error } = await supabase.from('cabify_invoices').upsert({ month, amount: n, updated_by: u?.user?.email || null, updated_at: new Date().toISOString() });
        setBusy('');
        if (error) { setNotice({ tone: 'danger', text: 'No se pudo guardar la factura: ' + error.message }); return; }
        setInvoices(prev => ({ ...prev, [month]: { amount: n, notes: prev[month]?.notes ?? null } }));
        setNotice({ tone: 'success', text: `Factura de ${monthLabel(month).toLowerCase()} guardada.` });
    };

    const exportCsv = () => {
        const head = ['fecha', 'envio', 'pedido_id', 'destino', 'modalidad', 'estado', 'cobrado_al_cliente', 'costo_estimado'];
        const lines = inMonth.map(r => [
            fmtDate(serviceDate(r)), r.external_id || '', r.order_id ?? '',
            (r.raw?.created?.parcels?.[0]?.dropoff_info?.addr || '').replace(/"/g, "'"),
            r.estimate?.shipping_type || '', CABIFY_STATE_ES[r.status] || r.status,
            (() => { const c = chargeOf.get(r.id); return !c ? '' : c.kind === 'cobrado' ? String(c.amount).replace('.', ',') : c.kind === 'bulto' ? '2do bulto' : c.kind; })(),
            r.estimate?.amount != null ? String(pesos(r.estimate.amount)).replace('.', ',') : '',
        ].map(v => `"${v}"`).join(';'));
        const blob = new Blob(['﻿' + [head.join(';'), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `cabify_${month}.csv`; a.click();
    };

    if (loading) return <div className="tw w-full min-w-0 pb-12 pt-1"><ScreenSkeleton /></div>;

    const cols: GridCol<Row>[] = [
        { key: 'fecha', header: 'Fecha', sort: r => serviceDate(r), render: r => <span className="tabular-nums">{fmtDate(serviceDate(r))}</span> },
        { key: 'envio', header: 'Envío', sort: r => r.external_id || '', render: r => <span className="font-semibold">{r.external_id || r.parcel_id.slice(0, 8)}</span> },
        { key: 'destino', header: 'Destino', hideOnMobile: true, sort: r => r.raw?.created?.parcels?.[0]?.dropoff_info?.addr || '', render: r => <span className="block max-w-[320px] truncate text-soft" title={r.raw?.created?.parcels?.[0]?.dropoff_info?.addr}>{r.raw?.created?.parcels?.[0]?.dropoff_info?.addr || '—'}</span> },
        { key: 'modalidad', header: 'Modalidad', hideOnMobile: true, sort: r => r.estimate?.shipping_type || '', render: r => <span className="text-soft">{r.estimate?.shipping_type || '—'}</span> },
        { key: 'estado', header: 'Estado', sort: r => r.status, render: r => <Badge size="sm" tone={r.status === 'delivered' ? 'success' : /return|undeliver/.test(r.status) ? 'warning' : 'neutral'}>{CABIFY_STATE_ES[r.status] || r.status}</Badge> },
        {
            key: 'cobrado', header: 'Cobrado al cliente', align: 'right', sort: r => chargeOf.get(r.id)?.amount ?? 0, render: r => {
                const c = chargeOf.get(r.id);
                if (!c) return null;
                if (c.kind === 'cobrado') return <span className="font-semibold tabular-nums text-[var(--success)]">{money(c.amount)}</span>;
                if (c.kind === 'gratis') return <Badge size="sm" tone="warning" title="Envío gratis: lo paga Krilic">Gratis</Badge>;
                if (c.kind === 'canje') return <Badge size="sm" title="Canje a creador">Canje</Badge>;
                if (c.kind === 'manual') return <span className="text-[length:var(--text-xs)] text-faint" title="Envío cargado a mano en /cabify, sin pedido">envío manual</span>;
                return <span className="text-[length:var(--text-xs)] text-faint" title="Mismo pedido: el envío se cobra una vez, en el primer bulto">2º bulto</span>;
            },
        },
        {
            key: 'costo', header: 'Costo', align: 'right', sort: r => r.estimate?.amount ?? -1, render: r => r.estimate?.amount != null
                ? <span className="font-semibold tabular-nums">{money(pesos(r.estimate.amount))}</span>
                : <span className="text-[length:var(--text-xs)] text-faint">sin cotizar</span>,
        },
    ];

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            <PageHeader title="Cabify"
                description="Cuánto se gasta en envíos de Cabify por mes, para controlar la factura. Cada envío se cotiza con sus datos reales (destino, peso, medidas y modalidad); Cabify no informa lo cobrado, así que la factura es la que confirma."
                actions={<>
                    {pendingAll > 0 && <Button variant="secondary" onClick={priceMissing} loading={busy === 'pricing'}><RefreshCw size={16} /> Cotizar faltantes ({pendingAll})</Button>}
                    <Button variant="secondary" onClick={exportCsv} disabled={!inMonth.length}><Download size={16} /> Exportar CSV</Button>
                </>} />

            {notice && <div className="mb-4"><Notice tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Notice></div>}

            <div className="mb-4 flex flex-wrap items-center gap-3">
                <MonthPicker months={months} value={month} onChange={setMonth} />
            </div>

            <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KpiCard label="Envíos (bultos)" value={inMonth.length.toLocaleString('es-AR')}
                    context={unpriced ? `${unpriced} todavía sin cotizar` : 'todos cotizados'} />
                <KpiCard label="Costo estimado" value={money(total)}
                    context={priced.length ? `promedio ${money(total / priced.length)} por bulto` : 'sin envíos cotizados'} />
                <KpiCard label="Factura de Cabify" value={invoice != null ? money(invoice) : '—'}
                    context={invoice != null ? 'cargada' : 'cargala abajo para comparar'} />
                <KpiCard label="Diferencia" value={diff == null ? '—' : `${diff >= 0 ? '+' : '−'}${money(diff)}`}
                    tone={diff == null ? undefined : Math.abs(diff) <= Math.max(1000, total * 0.03) ? 'profit' : 'warn'}
                    context={diff == null ? 'factura − estimado' : Math.abs(diff) <= Math.max(1000, total * 0.03)
                        ? 'cierra (dentro del 3%)' : diff > 0 ? 'la factura cobra de más que lo cotizado: revisar' : 'la factura cobra menos que lo cotizado'} />
            </section>

            <div className="mb-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <Panel title={`Factura de ${month ? monthLabel(month).toLowerCase() : '—'}`} subtitle="El total que figura en la factura de Cabify de ese mes">
                    <div className="flex flex-wrap items-center gap-2">
                        <MoneyField className="w-48" ariaLabel="Total de la factura" placeholder="0" value={invDraft} onChange={setInvDraft}
                            onKeyDown={e => { if (e.key === 'Enter') saveInvoice(); }} />
                        <Button onClick={saveInvoice} loading={busy === 'saving'} disabled={!invDraft}><Save size={16} /> Guardar</Button>
                    </div>
                </Panel>
                <Panel title="Por modalidad" subtitle="Cantidad y costo estimado del mes">
                    {byModality.length ? (
                        <ul className="m-0 grid list-none gap-1.5 p-0 text-[length:var(--text-sm)]">
                            {byModality.map(([k, v]) => (
                                <li key={k} className="flex items-baseline justify-between gap-3">
                                    <span className="text-ink">{k} <span className="text-soft">· {v.n} bulto{v.n === 1 ? '' : 's'}</span></span>
                                    <span className="font-semibold tabular-nums">{money(v.total)}</span>
                                </li>
                            ))}
                        </ul>
                    ) : <p className="m-0 text-[length:var(--text-sm)] text-faint">Sin envíos cotizados.</p>}
                </Panel>
            </div>

            <Panel className="mb-5" title="Cobrado al cliente vs costo de Cabify"
                subtitle={`${month ? monthLabel(month) : ''}: cuánto del costo de los envíos recuperás cobrándolos`}>
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
                    <div>
                        <div className="flex items-baseline gap-2">
                            <span className={`text-[length:var(--text-2xl)] font-semibold tabular-nums ${recovery.pct >= 100 ? 'text-[var(--success)]' : 'text-ink'}`}>{Math.round(recovery.pct)}%</span>
                            <span className="text-[length:var(--text-sm)] text-soft">del costo lo pagan los clientes</span>
                        </div>
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                            <div className="h-full bg-[var(--success)]" style={{ width: `${Math.min(100, recovery.pct)}%` }} />
                        </div>
                        <p className="m-0 mt-3 text-[length:var(--text-sm)] text-soft">
                            Cobraste <b className="text-ink">{money(recovery.totalCobrado)}</b> de envío sobre un costo de <b className="text-ink">{money(total)}</b>.
                            {recovery.puesto > 0
                                ? <> Krilic puso <b className="text-[var(--danger)]">{money(recovery.puesto)}</b> de su bolsillo.</>
                                : <> Sobraron <b className="text-[var(--success)]">{money(-recovery.puesto)}</b>.</>}
                        </p>
                    </div>
                    <ul className="m-0 grid list-none gap-2 p-0 text-[length:var(--text-sm)]">
                        <li className="flex items-baseline justify-between gap-3">
                            <span className="text-ink">Envíos cobrados <span className="text-soft">· {recovery.cobrado.orders} pedidos</span></span>
                            <span className="tabular-nums">cobrado <b>{money(recovery.cobrado.cobrado)}</b> · costo {money(recovery.cobrado.costo)}
                                <span className={recovery.cobrado.cobrado >= recovery.cobrado.costo ? ' text-[var(--success)]' : ' text-[var(--danger)]'}> ({recovery.cobrado.cobrado >= recovery.cobrado.costo ? '+' : '−'}{money(recovery.cobrado.cobrado - recovery.cobrado.costo)})</span></span>
                        </li>
                        <li className="flex items-baseline justify-between gap-3">
                            <span className="text-ink">Envíos gratis <span className="text-soft">· {recovery.gratis.orders} pedidos</span></span>
                            <span className="tabular-nums">costo <b className="text-[var(--danger)]">{money(recovery.gratis.costo)}</b>{recovery.gratis.orders ? <span className="text-soft"> · {money(recovery.gratis.costo / recovery.gratis.orders)} c/u</span> : null}</span>
                        </li>
                        {recovery.canje.orders > 0 && <li className="flex items-baseline justify-between gap-3">
                            <span className="text-ink">Canjes <span className="text-soft">· {recovery.canje.orders}</span></span>
                            <span className="tabular-nums">costo <b>{money(recovery.canje.costo)}</b></span>
                        </li>}
                        {recovery.manual.n > 0 && <li className="flex items-baseline justify-between gap-3">
                            <span className="text-ink">Envíos manuales sin pedido <span className="text-soft">· {recovery.manual.n}</span></span>
                            <span className="tabular-nums">costo <b>{money(recovery.manual.costo)}</b></span>
                        </li>}
                    </ul>
                </div>
            </Panel>

            <DataGrid rows={inMonth} cols={cols} searchPlaceholder="Buscar envío, destino o estado…"
                emptyTitle="Sin envíos de Cabify en el mes" />
            <p className="mt-3 text-[length:var(--text-xs)] text-faint">
                Se cuentan los envíos despachados (también devueltos o con incidencias). No se cuentan las etiquetas borradas o que nunca se despacharon.
                Cada bulto es un envío: un pedido de 2 bultos son 2 líneas. Los envíos anteriores al 14/10 se cotizaron después, con la tarifa de ese día.
            </p>
        </div>
    );
}
